/**
 * Bedienung des Gravur-Konfigurators.
 *
 * Der Rechenkern liegt in gravur-logik.js und ist ohne Browser geprueft.
 * Diese Datei kuemmert sich nur um Anzeige, Vorschau und Warenkorb.
 *
 * Der Rechenkern wird bewusst zur Laufzeit geladen, nicht per festem import:
 * ein fester import zeigt auf './gravur-logik.js' ohne Versionsstempel, und
 * unter dieser Adresse liefert Shopify mit langer Aufbewahrungszeit aus.
 * Wiederkehrende Besucher haetten dann nach einer Korrektur noch die alte
 * Preisberechnung im Zwischenspeicher. Die Adresse mit Stempel steht in der
 * Konfiguration (logikUrl) und kommt aus {{ 'gravur-logik.js' | asset_url }}.
 */

let logik = null;

async function ladeLogik(adresse) {
  if (!logik) logik = await import(adresse || './gravur-logik.js');
  return logik;
}

class GravurKonfigurator {
  constructor(wurzel, konfig, rechenkern) {
    this.wurzel = wurzel;
    this.konfig = konfig;
    this.logik = rechenkern;
    this.auswahl = {};
    this.vorschauEbene = null;

    // Steht dem Kunden die Wahl offen, faengt der Konfigurator bei der Fraesung
    // an. Artikel, die nur ein Verfahren zulassen, tragen es in ihrer
    // Konfiguration - die Geschenkartikel werden ausschliesslich gelasert.
    this.verfahren = rechenkern.startVerfahren(konfig);
    this.schrift = null;

    this.starteAuswahl();
    this.verbindeFelder();
    this.verbindeSchriftwahl();
    this.verbindeVerfahren();
    this.baueVorschau();
    this.uebernehmeWarenkorbKnopf();
    this.zeichneNeu();
  }

  /** Anfangszustand aus dem Markup lesen, damit Vorauswahl und Zurueck-Navigation stimmen. */
  starteAuswahl() {
    for (const option of this.konfig.optionen) {
      if (option.art === 'gravur') {
        // Offene Felder haben kein Kaestchen; sie gelten als gewaehlt, sobald Text darin steht.
        const an = option.immer
          ? true
          : this.wurzel.querySelector(`[data-an="${option.id}"]`)?.checked ?? false;
        this.auswahl[option.id] = { an, text: '' };
      } else {
        const wert = this.wurzel.querySelector(`[data-auswahl="${option.id}"]`)?.value || '';
        if (wert) this.auswahl[option.id] = wert;
      }
    }
  }

  verbindeFelder() {
    for (const option of this.konfig.optionen) {
      if (option.art === 'gravur') {
        const kaestchen = this.wurzel.querySelector(`[data-an="${option.id}"]`);
        const feld = this.wurzel.querySelector(`[data-text="${option.id}"]`);

        kaestchen?.addEventListener('change', () => {
          this.auswahl[option.id].an = kaestchen.checked;
          this.zeichneNeu();
          if (kaestchen.checked) feld?.focus();
        });

        feld?.addEventListener('input', () => {
          this.auswahl[option.id].text = feld.value;
          this.zeichneNeu();
        });
      } else {
        const auswahlfeld = this.wurzel.querySelector(`[data-auswahl="${option.id}"]`);
        auswahlfeld?.addEventListener('change', () => {
          if (auswahlfeld.value) this.auswahl[option.id] = auswahlfeld.value;
          else delete this.auswahl[option.id];
          this.zeichneNeu();
        });
      }
    }
  }

  // --- Verfahren ------------------------------------------------------------

  /**
   * Zwei Karten, ein Zustand. Die Wahl entscheidet ueber die Schriftauswahl,
   * ueber die Beschriftung der Felder und ueber das Aussehen der Vorschau.
   */
  verbindeVerfahren() {
    const schalter = [...this.wurzel.querySelectorAll('[data-verfahren]')];

    // Ohne Karten steht das Verfahren fest. Angewandt werden muss es trotzdem:
    // Feldbeschriftungen, Schrift, Holzstreifen und die Marke darueber richten
    // sich danach, und beim festen Laser bleibt die Schriftwahl sichtbar.
    if (!schalter.length) {
      this.setzeVerfahren(this.verfahren);
      return;
    }

    for (const s of schalter) {
      s.addEventListener('change', () => {
        if (s.checked) this.setzeVerfahren(s.value);
      });
    }
    this.setzeVerfahren(schalter.find((s) => s.checked)?.value || 'fraesung');
  }

  setzeVerfahren(verfahren) {
    this.verfahren = verfahren === 'laser' ? 'laser' : 'fraesung';
    const laser = this.verfahren === 'laser';

    // Die Schrift waehlt der Kunde nur beim Laser; die Fraesung hat eine feste.
    if (this.schriftwahl) {
      this.schriftwahl.hidden = !laser;
      if (!laser) this.klappeSchriften(false);
    }

    const holz = this.wurzel.querySelector('[data-holz]');
    if (holz) {
      holz.classList.toggle('gravur__holz--laser', laser);
      holz.classList.toggle('gravur__holz--fraesung', !laser);
    }

    const marke = this.wurzel.querySelector('[data-verfahren-marke]');
    if (marke) marke.textContent = laser ? 'So wird gelasert' : 'So wird gefräst';

    // "Fräsung oben" wird zu "Lasergravur oben" - im Kaestchen wie spaeter in
    // der Bestellung. Was der Kunde liest, muss die Werkstatt ausfuehren.
    for (const option of this.konfig.optionen) {
      if (option.art !== 'gravur') continue;
      const feld = this.wurzel.querySelector(`[data-beschriftung="${option.id}"]`);
      if (feld) feld.textContent = this.logik.stelleBenennen(option.beschriftung, this.verfahren);
    }

    this.wendeSchriftAn();
    this.zeichneNeu();
  }

  // --- Schriftwahl ----------------------------------------------------------

  /**
   * Eigenes Auswahlmenue: Knopf klappt eine Liste auf, jeder Eintrag zeigt die
   * Schrift an einer Probe. Bedienbar mit Maus, Finger und Tastatur.
   */
  verbindeSchriftwahl() {
    const wahl = this.wurzel.querySelector('[data-schriftwahl]');
    if (!wahl) return;

    this.schriftwahl = wahl;
    this.schriftKnopf = wahl.querySelector('[data-schrift-knopf]');
    this.schriftListe = wahl.querySelector('[data-schrift-liste]');
    const eintraege = [...this.schriftListe.querySelectorAll('[data-schrift]')];

    const ausgewaehlt = eintraege.find((e) => e.getAttribute('aria-selected') === 'true') || eintraege[0];
    this.setzeSchrift(ausgewaehlt);

    this.schriftKnopf.addEventListener('click', () => this.klappeSchriften(!this.schriftOffen));

    for (const eintrag of eintraege) {
      eintrag.addEventListener('click', () => {
        this.setzeSchrift(eintrag);
        this.klappeSchriften(false);
        this.schriftKnopf.focus();
      });
    }

    this.schriftListe.addEventListener('keydown', (ereignis) => {
      const stelle = eintraege.indexOf(document.activeElement);
      if (ereignis.key === 'ArrowDown' || ereignis.key === 'ArrowUp') {
        ereignis.preventDefault();
        const schritt = ereignis.key === 'ArrowDown' ? 1 : -1;
        const ziel = (stelle + schritt + eintraege.length) % eintraege.length;
        eintraege[ziel].focus();
      } else if (ereignis.key === 'Enter' || ereignis.key === ' ') {
        ereignis.preventDefault();
        if (stelle >= 0) {
          this.setzeSchrift(eintraege[stelle]);
          this.klappeSchriften(false);
          this.schriftKnopf.focus();
        }
      } else if (ereignis.key === 'Escape') {
        this.klappeSchriften(false);
        this.schriftKnopf.focus();
      }
    });

    // Klick daneben schliesst das Menue.
    document.addEventListener('click', (ereignis) => {
      if (this.schriftOffen && !wahl.contains(ereignis.target)) this.klappeSchriften(false);
    });
  }

  klappeSchriften(offen) {
    this.schriftOffen = offen;
    this.schriftListe.hidden = !offen;
    this.schriftKnopf.setAttribute('aria-expanded', String(offen));
    if (offen) {
      this.zeigeProben();
      this.schriftListe.querySelector('[aria-selected="true"]')?.focus();
    }
  }

  /** Uebernimmt eine Schrift in Knopf, Eingabefelder und Holzstreifen. */
  setzeSchrift(eintrag) {
    this.schrift = {
      kennung: eintrag.dataset.schrift,
      anzeige: eintrag.dataset.schriftAnzeige,
    };

    for (const anderer of this.schriftListe.querySelectorAll('[data-schrift]')) {
      anderer.setAttribute('aria-selected', String(anderer === eintrag));
    }

    this.schriftKnopf.querySelector('[data-schrift-name]').textContent = this.schrift.anzeige;
    this.wendeSchriftAn();
  }

  /**
   * Traegt die passende Schrift in Eingabefelder, Holzstreifen und Proben ein.
   *
   * Beim Laser ist das die gewaehlte Schrift, bei der Fraesung die feste.
   * Die Probe auf der Laser-Karte zeigt immer die gewaehlte Schrift, auch
   * solange gefraest wird - sie soll ja zeigen, was der Wechsel braechte.
   */
  wendeSchriftAn() {
    const laser = this.verfahren === 'laser';
    const gewaehlt = `gravur-schrift--${this.schrift?.kennung || 'fraesung'}`;
    const amWerk = laser ? gewaehlt : 'gravur-schrift--fraesung';

    const umstellen = (element, klasse) => {
      if (!element) return;
      for (const vorhanden of [...element.classList]) {
        if (vorhanden.startsWith('gravur-schrift--')) element.classList.remove(vorhanden);
      }
      element.classList.add(klasse);
    };

    // Der Kunde tippt in der Schrift, die später ins Holz geht.
    for (const feld of this.wurzel.querySelectorAll('[data-text]')) umstellen(feld, amWerk);
    for (const zeile of this.wurzel.querySelectorAll('[data-holzzeile]')) umstellen(zeile, amWerk);

    umstellen(this.schriftKnopf?.querySelector('[data-schrift-probe]'), gewaehlt);
    for (const probe of this.wurzel.querySelectorAll('[data-laserprobe]')) umstellen(probe, gewaehlt);

    this.zeigeProben();
  }

  /** Die Proben zeigen den eingegebenen Text, solange einer da ist. */
  zeigeProben() {
    const eingegeben = this.konfig.optionen
      .filter((o) => o.art === 'gravur')
      .map((o) => String(this.auswahl[o.id]?.text ?? '').trim())
      .find(Boolean);

    for (const probe of this.wurzel.querySelectorAll('[data-probe], [data-schrift-probe]')) {
      // Auf den schmalen Karten passt kein ganzer Beispielsatz.
      const ersatz = probe.hasAttribute('data-kurz') ? 'Mustermann' : 'Familie Mustermann';
      probe.textContent = eingegeben || ersatz;
    }
  }

  // --- Vorschau -----------------------------------------------------------

  /** Sucht das grosse Produktbild und legt eine Textebene darueber. */
  baueVorschau() {
    if (!this.konfig.vorschau?.an) return;

    const bild = this.findeProduktbild();
    if (!bild) return;

    const huelle = bild.closest('picture')?.parentElement || bild.parentElement;
    if (!huelle) return;

    if (getComputedStyle(huelle).position === 'static') huelle.style.position = 'relative';

    const ebene = document.createElement('div');
    ebene.className = 'gravur-vorschau';
    ebene.setAttribute('aria-hidden', 'true');

    for (const option of this.konfig.optionen) {
      if (option.art !== 'gravur') continue;
      const text = document.createElement('span');
      text.className = 'gravur-vorschau__text';
      text.dataset.vorschau = option.id;
      const v = option.vorschau || {};
      text.style.left = `${v.x ?? 30}%`;
      text.style.top = `${v.y ?? 40}%`;
      text.style.width = `${v.breite ?? 30}%`;
      text.style.transform = `translate(-50%, -50%) rotate(${v.drehung ?? 0}deg)`;
      text.style.fontSize = `${v.groesse ?? 4}cqw`;
      text.style.color = this.konfig.vorschau.farbe || '#3B2A16';
      text.style.fontFamily = this.konfig.vorschau.schrift || 'sans-serif';
      ebene.appendChild(text);
    }

    huelle.appendChild(ebene);
    this.vorschauEbene = ebene;
  }

  findeProduktbild() {
    const kandidaten = [
      '.product-media img',
      '.product__media img',
      '.product-gallery img',
      '[class*="product"] [class*="media"] img',
    ];
    for (const wahl of kandidaten) {
      const treffer = [...document.querySelectorAll(wahl)]
        .filter((b) => b.clientWidth > 200)
        .sort((a, b) => b.clientWidth - a.clientWidth);
      if (treffer.length) return treffer[0];
    }
    return null;
  }

  aktualisiereVorschau() {
    this.aktualisiereHolz();
    this.zeigeProben();
    if (!this.vorschauEbene) return;
    for (const option of this.konfig.optionen) {
      if (option.art !== 'gravur') continue;
      const feld = this.vorschauEbene.querySelector(`[data-vorschau="${option.id}"]`);
      if (!feld) continue;
      const eintrag = this.auswahl[option.id];
      const zeigen = option.immer || eintrag?.an;
      feld.textContent = zeigen ? eintrag?.text ?? '' : '';
    }
  }

  /**
   * Holzstreifen unter den Feldern. Er erscheint erst, wenn wirklich etwas
   * gefraest wird - ein leeres Stueck Holz zu zeigen hilft niemandem.
   */
  aktualisiereHolz() {
    const streifen = this.wurzel.querySelector('[data-holzvorschau]');
    if (!streifen) return;

    let etwasDrin = false;
    for (const option of this.konfig.optionen) {
      if (option.art !== 'gravur') continue;
      const zeile = streifen.querySelector(`[data-holzzeile="${option.id}"]`);
      if (!zeile) continue;
      const eintrag = this.auswahl[option.id];
      const text = option.immer || eintrag?.an ? String(eintrag?.text ?? '').trim() : '';
      zeile.textContent = text;
      zeile.hidden = !text;
      if (text) etwasDrin = true;
    }
    streifen.hidden = !etwasDrin;
  }

  // --- Anzeige ------------------------------------------------------------

  zeichneNeu() {
    for (const option of this.konfig.optionen) {
      if (option.art !== 'gravur') continue;

      const eintrag = this.auswahl[option.id];
      const eingabe = this.wurzel.querySelector(`[data-eingabe="${option.id}"]`);
      if (eingabe) eingabe.hidden = option.immer ? false : !eintrag.an;

      const zaehler = this.wurzel.querySelector(`[data-zaehler="${option.id}"]`);
      if (zaehler && option.maxZeichen) {
        const rest = option.maxZeichen - eintrag.text.length;
        zaehler.textContent = `noch ${rest} Zeichen`;
        zaehler.classList.toggle('gravur__zaehler--knapp', rest <= 3);
      }
    }

    const liste = this.wurzel.querySelector('[data-zeilen]');
    if (liste) {
      liste.innerHTML = '';
      for (const zeile of this.logik.zeilenAusAuswahl(this.auswahl, this.konfig, this.verfahren)) {
        const eintrag = document.createElement('li');
        eintrag.className = `gravur__zeile gravur__zeile--${zeile.art}`;
        const name = document.createElement('span');
        name.textContent = zeile.text;
        const betrag = document.createElement('span');
        betrag.textContent = this.logik.preisText(zeile.betrag);
        eintrag.append(name, betrag);
        liste.appendChild(eintrag);
      }
    }

    const summe = this.wurzel.querySelector('[data-summe]');
    if (summe) {
      summe.textContent = this.logik.preisText(this.logik.gesamtpreis(this.auswahl, this.konfig));
    }

    this.aktualisiereVorschau();
  }

  zeigeFehler(fehler) {
    this.wurzel.querySelectorAll('[data-fehler]').forEach((el) => {
      el.hidden = true;
      el.textContent = '';
    });
    for (const f of fehler) {
      const el = this.wurzel.querySelector(`[data-fehler="${f.optionId}"]`);
      if (el) {
        el.textContent = f.meldung;
        el.hidden = false;
      }
    }
    const sammel = this.wurzel.querySelector('[data-sammelfehler]');
    if (sammel) {
      sammel.hidden = fehler.length === 0;
      sammel.textContent = fehler.length ? 'Bitte die markierten Angaben ergänzen.' : '';
    }
    if (fehler.length) {
      const erstes = this.wurzel.querySelector(`[data-option="${fehler[0].optionId}"]`);
      erstes?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // --- Warenkorb ----------------------------------------------------------

  /**
   * Das Theme legt nur den Hauptartikel in den Warenkorb, und zwar über einen
   * eigenen Klick-Handler, nicht über ein submit-Ereignis. Deshalb hier beides
   * in der Erfassungsphase auf document abfangen: das ist die früheste Stufe,
   * vor allen Handlern des Themes.
   */
  uebernehmeWarenkorbKnopf() {
    const formular = document.querySelector('form[action*="/cart/add"]');
    if (!formular) return;
    this.formular = formular;

    document.addEventListener('click', (ereignis) => this.beiKlick(ereignis), true);
    document.addEventListener(
      'submit',
      (ereignis) => {
        if (!this.istWarenkorbFormular(ereignis.target)) return;
        ereignis.preventDefault();
        ereignis.stopImmediatePropagation();
        this.legeInDenWarenkorb(this.formular);
      },
      true
    );
  }

  istWarenkorbFormular(element) {
    return element instanceof HTMLFormElement && /\/cart\/add/.test(element.getAttribute('action') || '');
  }

  /** Ist das der Knopf, der diesen Artikel in den Warenkorb legt? */
  istWarenkorbKnopf(element) {
    if (!(element instanceof Element)) return false;
    const knopf = element.closest('button, input[type="submit"], a');
    if (!knopf) return false;
    if (knopf.closest('#cart-upselling, .cart-drawer, [data-gravur]')) return false;
    if (knopf.id === 'product-information-cart-button') return true;
    if (knopf.classList.contains('p_a_t_c')) return true;
    return knopf.type === 'submit' && Boolean(knopf.closest('form[action*="/cart/add"]'));
  }

  beiKlick(ereignis) {
    if (!this.istWarenkorbKnopf(ereignis.target)) return;
    ereignis.preventDefault();
    ereignis.stopImmediatePropagation();
    this.legeInDenWarenkorb(this.formular);
  }

  async legeInDenWarenkorb(formular) {
    const fehler = this.logik.pruefeAuswahl(this.auswahl, this.konfig, this.verfahren);
    if (fehler.length) {
      this.zeigeFehler(fehler);
      return;
    }
    this.zeigeFehler([]);

    const mengenfeld = formular.querySelector('[name="quantity"]');
    const menge = Math.max(1, parseInt(mengenfeld?.value, 10) || 1);
    const setId = `g${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

    const knopf = formular.querySelector('[type="submit"]');
    const vorherigerText = knopf?.textContent;
    if (knopf) {
      knopf.disabled = true;
      knopf.textContent = 'Wird hinzugefügt …';
    }

    try {
      const antwort = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        // Shopify stellt die zuletzt uebergebene Position im Warenkorb nach
        // oben. Der Rechenkern liefert den Hauptartikel zuerst - fachlich
        // richtig, in der Anzeige aber unter seinen eigenen Aufpreisen.
        // Umgedreht abgeschickt steht der Briefkasten oben und die Gravuren
        // darunter, in der Reihenfolge, in der sie gefraest werden.
        body: JSON.stringify({
          items: this.logik
            .warenkorbPositionen(this.auswahl, this.konfig, setId, menge, {
              verfahren: this.verfahren,
              schrift: this.schrift?.anzeige,
            })
            .reverse(),
        }),
      });

      if (!antwort.ok) {
        const daten = await antwort.json().catch(() => ({}));
        throw new Error(daten.description || 'Der Artikel konnte nicht hinzugefügt werden.');
      }

      document.dispatchEvent(new CustomEvent('gravur:hinzugefuegt'));
      window.location.href = '/cart';
    } catch (fehlerObjekt) {
      const sammel = this.wurzel.querySelector('[data-sammelfehler]');
      if (sammel) {
        sammel.textContent = fehlerObjekt.message;
        sammel.hidden = false;
      }
    } finally {
      if (knopf) {
        knopf.disabled = false;
        if (vorherigerText) knopf.textContent = vorherigerText;
      }
    }
  }
}

async function starte() {
  for (const wurzel of document.querySelectorAll('[data-gravur]')) {
    if (wurzel.dataset.bereit === 'ja') continue;
    const daten = document.querySelector(`[data-gravur-konfig="${wurzel.id}"]`);
    if (!daten) continue;
    try {
      const konfig = JSON.parse(daten.textContent);
      const rechenkern = await ladeLogik(konfig.logikUrl);
      new GravurKonfigurator(wurzel, konfig, rechenkern);
      wurzel.dataset.bereit = 'ja';
    } catch (fehler) {
      console.error('Gravur-Konfigurator: Start fehlgeschlagen', fehler);
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', starte);
} else {
  starte();
}

// Im Theme-Editor werden Abschnitte neu geladen.
document.addEventListener('shopify:section:load', starte);
